import Form from "@/components/Form";
import SaveButton from "@/components/SaveButton";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { TIME_FORMAT } from "@/constants";
import { useProjectSchedulesContext } from "@/modules/projects/components/schedules/ProjectSchedulesContext";
import { ProjectSchedulesVisitForm } from "@/modules/projects/components/schedules/ProjectSchedulesVisitForm";
import { ProjectSchedulesVisitDetails } from "@/modules/projects/components/schedules/ProjectSchedulesVisitDetails";
import { formatTimeHHMM } from "@/modules/projects/lib/formatTimeHHMM";
import { parseTimeToDate } from "@/modules/projects/lib/parseTimeToDate";
import { ProjectSchedulesService } from "@/modules/projects/service/projectSchedule";
import { writeProjectScheduleVisit } from "@/schemas/projectSchedule";
import {
  ReadProjectSchedule,
  ReadProjectScheduleVisit,
  WriteProjectScheduleVisit,
} from "@/types/projectSchedule";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, subHours } from "date-fns";
import { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useGetSchedulesContractorVisitQuery } from "../../hooks/useGetSchedulesContractorVisitQuery";
import Spinner from "@/components/Spinner";
import { DeleteButton } from "@/components/DeleteButton";
import { toast } from "@/components/ui/use-toast";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { PopoverClose } from "@radix-ui/react-popover";
import { useCancelScheduleVisitMutation } from "./hooks/useCancelScheduleVisitMutation";
import {
  VISIT_STATUS_NEW,
  VISIT_STATUS_COMPLETED,
} from "@/schemas/contractorVisit";
import ConfirmDialog from "@/components/confirm-dialog";
import { XCircle } from "lucide-react";

interface ProjectSchedulesVisitEditorProps {
  projectSchedule: ReadProjectSchedule;
  projectScheduleVisit: ReadProjectScheduleVisit;
}

const ProjectSchedulesVisitEditorComponent = ({
  projectSchedule,
  projectScheduleVisit,
}: ProjectSchedulesVisitEditorProps) => {
  const { t } = useTranslation();
  const { projectId, refetch } = useProjectSchedulesContext();

  const { data: visit, isLoading: isContractorVisitLoading } =
    useGetSchedulesContractorVisitQuery(
      projectId,
      projectSchedule.id,
      projectScheduleVisit.id,
    );

  const updateMutation = useMutationWithErrorHandling({
    onSuccess: () => refetch(),
    mutationFn: async (values: WriteProjectScheduleVisit) =>
      ProjectSchedulesService.updateVisit(
        projectId,
        projectSchedule.id,
        projectScheduleVisit.id,
        {
          suggested_start_time: values.suggested_start_time,
          suggested_end_time: values.suggested_end_time,
          kam_planned_duration: values.kam_planned_duration,
        },
      ),
    errorTitle: t("projectSchedules.error.updateVisitTitle"),
  });

  const deleteMutation = useMutationWithErrorHandling({
    mutationFn: () => {
      return ProjectSchedulesService.deleteSchedule(
        projectId,
        projectSchedule.id,
        projectScheduleVisit.id,
      );
    },
    errorTitle: t("projectSchedules.error.deleteVisitTitle"),
    onSuccess: () => {
      toast({
        title: t("projectSchedules.success.visitDeleted"),
      });
      refetch();
    },
  });

  const cancelMutation = useCancelScheduleVisitMutation(
    projectSchedule.id,
    projectScheduleVisit.id,
  );

  const form = useForm<WriteProjectScheduleVisit>({
    resolver: zodResolver(writeProjectScheduleVisit),
    defaultValues: {
      suggested_start_time: "",
      suggested_end_time: "",
      kam_planned_duration: "",
    },
    disabled: updateMutation.isPending,
  });

  useEffect(() => {
    if (!visit) {
      return;
    }
    const { suggested_start_time, suggested_end_time } = visit;
    const { kam_planned_duration } = projectScheduleVisit;

    form.reset({
      suggested_start_time: suggested_start_time
        ? formatTimeHHMM(suggested_start_time)
        : "",
      suggested_end_time: suggested_end_time
        ? formatTimeHHMM(suggested_end_time)
        : "",
      kam_planned_duration,
    });
  }, [visit, projectScheduleVisit, form]);

  const timeStart = form.watch("suggested_start_time");
  const timeEnd = form.watch("suggested_end_time");

  useEffect(() => {
    if (timeStart && timeEnd) {
      const startDate = parseTimeToDate(timeStart);
      const endDate = parseTimeToDate(timeEnd);

      // If end time is earlier than start time, auto-set start time to 1 hour before end time
      if (endDate < startDate) {
        const newStartDate = subHours(endDate, 1);
        const newStartTime = format(newStartDate, TIME_FORMAT);

        form.setValue("suggested_start_time", newStartTime, {
          shouldValidate: true,
          shouldDirty: true,
        });
        return;
      }
    }
  }, [timeStart, timeEnd, form]);

  const onDeleteClick = useCallback(() => {
    deleteMutation.mutate(undefined);
  }, [deleteMutation]);

  const handleCancel = useCallback(() => {
    cancelMutation.mutate(undefined);
  }, [cancelMutation]);

  if (isContractorVisitLoading) {
    return <Spinner />;
  }

  const disabled =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    cancelMutation.isPending ||
    isContractorVisitLoading;

  const isEditAllowed = visit?.edit_allowed;

  const isMutatingButtonDisabled = disabled || !isEditAllowed;

  const canCancel =
    projectScheduleVisit.status === VISIT_STATUS_NEW ||
    projectScheduleVisit.status === VISIT_STATUS_COMPLETED;

  return (
    <Form form={form} handleSubmit={updateMutation.mutateAsync}>
      <ProjectSchedulesVisitForm
        prPlannedDuration={visit?.pr_planned_duration}
        disabled={!isEditAllowed}
      />
      <Separator />
      <ProjectSchedulesVisitDetails visit={visit} />
      <Separator />
      <div className="flex flex-row gap-2 justify-between">
        <div className="flex flex-row gap-2">
          <DeleteButton
            disabled={isMutatingButtonDisabled}
            onClick={onDeleteClick}
            dialogContent={t(
              "projectSchedules.confirmDeleteSingleScheduleDialog.content",
            )}
          />
          {canCancel && (
            <ConfirmDialog
              title={t("projectContractorVisits.actions.cancel")}
              content={t(
                "projectContractorVisits.actions.cancelConfirmContent",
              )}
              onConfirm={handleCancel}
            >
              <Button variant="outline" disabled={isMutatingButtonDisabled}>
                <XCircle className="h-4 w-4 mr-2" />
                {t("projectContractorVisits.actions.cancel")}
              </Button>
            </ConfirmDialog>
          )}
        </div>
        <div className="grow" />
        <div className="flex flex-row gap-2">
          <PopoverClose asChild>
            <Button variant="outline" disabled={disabled}>
              {t("common.cancel")}
            </Button>
          </PopoverClose>
          <SaveButton disabled={isMutatingButtonDisabled} />
        </div>
      </div>
    </Form>
  );
};

export const ProjectSchedulesVisitEditor = memo(
  ProjectSchedulesVisitEditorComponent,
);
